import { Injectable, inject } from '@angular/core';
import { nanoid } from 'nanoid';
import {
  CreateInteractionParams,
  InteractionRecord as FormSubmissionRecord,
  UpdateInteractionParams,
} from '../models/interaction.model';
import { AuthService } from './auth.service';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Service for managing interaction records (offline-first)
 * All operations work with local SQLite database
 */
@Injectable({
  providedIn: 'root',
})
export class FormSumbissionService {
  private db = inject(DatabaseService);
  private authService = inject(AuthService);
  private logger = inject(LoggerService).getLogger('FormSubmissionService');

  /**
   * Get all formSubmission records
   */
  async getAll(): Promise<FormSubmissionRecord[]> {
    const results = await this.db.exec(
      'SELECT * FROM interaction_records ORDER BY created_at DESC',
    );
    return Promise.all(results.map((row) => this.mapRowToFormSubmission(row)));
  }

  /**
   * Create a reactive query for all formSubmission records
   * Returns signals that automatically update when data changes via sync
   */
  createQuery(): QuerySignal<FormSubmissionRecord[]> {
    return this.db.createQuery('interaction_records', () => this.getAll());
  }

  /**
   * Create a reactive query for formSubmission records filtered by asset and type
   * Returns signals that automatically update when data changes via sync
   */
  createQueryByCheckPointAndType(
    checkpointId: string,
    interactionTypeId: string,
  ): QuerySignal<FormSubmissionRecord[]> {
    this.logger.debug(
      'createQueryByCheckpointAndType called - checkpointId:',
      checkpointId,
      'interactionTypeId:',
      interactionTypeId,
    );
    return this.db.createQuery('interaction_records', () =>
      this.getByCheckPointAndType(checkpointId, interactionTypeId),
    );
  }

  /**
   * Get interaction record by ID
   */
  async getById(id: string): Promise<FormSubmissionRecord | null> {
    const results = await this.db.exec('SELECT * FROM interaction_records WHERE id = ?', [id]);

    if (results.length === 0) {
      return null;
    }

    return this.mapRowToFormSubmission(results[0]);
  }

  /**
   * Get all FormSubmissionHistory for a specific asset
   */
  async getByCheckPointId(checkpointId: string): Promise<FormSubmissionRecord[]> {
    const results = await this.db.exec(
      'SELECT * FROM interaction_records WHERE asset_id = ? ORDER BY created_at DESC',
      [checkpointId],
    );

    return Promise.all(results.map((row) => this.mapRowToFormSubmission(row)));
  }

  /**
   * Get all FormSubmissionHistory of a specific type
   */
  async getByFormId(interactionTypeId: string): Promise<FormSubmissionRecord[]> {
    const results = await this.db.exec(
      'SELECT * FROM interaction_records WHERE interaction_type_id = ? ORDER BY created_at DESC',
      [interactionTypeId],
    );

    return Promise.all(results.map((row) => this.mapRowToFormSubmission(row)));
  }

  /**
   * Get all FormSubmissionHistory for a specific asset and interaction type
   */
  async getByCheckPointAndType(
    checkpointId: string,
    interactionTypeId: string,
  ): Promise<FormSubmissionRecord[]> {
    this.logger.debug(
      'getByCheckPointAndType called - checkpointId:',
      checkpointId,
      'interactionTypeId:',
      interactionTypeId,
    );
    const results = await this.db.exec(
      'SELECT * FROM interaction_records WHERE asset_id = ? AND interaction_type_id = ? ORDER BY created_at DESC',
      [checkpointId, interactionTypeId],
    );
    this.logger.debug('getByCheckPointAndType - SQL results count:', results.length);
    this.logger.debug('getByCheckPointAndType - SQL results:', results);

    const mapped = await Promise.all(results.map((row) => this.mapRowToFormSubmission(row)));
    this.logger.debug('getByAssetAndType - mapped records:', mapped);
    return mapped;
  }

  /**
   * Get all FormSubmissionHistory for a specific asset, type, and asset instance (serial number)
   */
  async getByCheckPointAndTypeAndInstance(
    checkpointId: string,
    interactionTypeId: string,
    instanceId: string,
  ): Promise<FormSubmissionRecord[]> {
    const results = await this.db.exec(
      'SELECT * FROM interaction_records WHERE asset_id = ? AND interaction_type_id = ? AND asset_instance_id = ? ORDER BY created_at DESC',
      [checkpointId, interactionTypeId, instanceId],
    );
    return Promise.all(results.map((row) => this.mapRowToFormSubmission(row)));
  }

  /**
   * Create a reactive query filtered by asset, type, and asset instance (serial number)
   */
  createQueryByCheckPointAndTypeAndInstance(
    checkpointId: string,
    interactionTypeId: string,
    instanceId: string,
  ): QuerySignal<FormSubmissionRecord[]> {
    return this.db.createQuery('interaction_records', () =>
      this.getByCheckPointAndTypeAndInstance(checkpointId, interactionTypeId, instanceId),
    );
  }

  /**
   * Get the most recent interaction for a specific asset, type, and instance
   * Used for pre-filling and calculated field lookups in instance workflows
   */
  async getLastByCheckPointAndTypeAndInstance(
    checkpointId: string,
    interactionTypeId: string,
    instanceId: string,
  ): Promise<FormSubmissionRecord | null> {
    const results = await this.db.exec(
      'SELECT * FROM interaction_records WHERE asset_id = ? AND interaction_type_id = ? AND asset_instance_id = ? ORDER BY created_at DESC LIMIT 1',
      [checkpointId, interactionTypeId, instanceId],
    );

    if (results.length === 0) {
      return null;
    }

    return this.mapRowToFormSubmission(results[0]);
  }

  /**
   * Get the most recent interaction for a specific asset and interaction type
   * Used for pre-filling new FormSubmissionHistory when prefill_last_values is enabled
   */
  async getLastByCheckPointAndType(
    checkpointId: string,
    interactionTypeId: string,
  ): Promise<FormSubmissionRecord | null> {
    const results = await this.db.exec(
      'SELECT * FROM interaction_records WHERE asset_id = ? AND interaction_type_id = ? ORDER BY created_at DESC LIMIT 1',
      [checkpointId, interactionTypeId],
    );

    if (results.length === 0) {
      return null;
    }

    return this.mapRowToFormSubmission(results[0]);
  }

  /**
   * Create a new interaction record
   */
  async create(params: CreateInteractionParams): Promise<FormSubmissionRecord> {
    const id = nanoid();

    // Extract binary data to BLOBs
    const processedFormData = await this.extractBinaryToBLOBs(params.form_data);
    const formDataJson = JSON.stringify(processedFormData);

    // Get current user ID (required for all FormSubmissionHistory)
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User must be authenticated to record FormSubmissionHistory');
    }

    // Validate parameters
    if (!params.checkPoint_id || typeof params.checkPoint_id !== 'string') {
      throw new Error(`Invalid asset_id: ${params.checkPoint_id}`);
    }
    if (!params.form_id || typeof params.form_id !== 'string') {
      throw new Error(`Invalid interaction_type_id: ${params.form_id}`);
    }

    this.logger.debug('Creating interaction record:', {
      id,
      checkPoint_id: params.checkPoint_id,
      form_id: params.form_id,
      user_id: userId,
      form_data_length: formDataJson.length,
      types: {
        id: typeof id,
        checkPoint_id: typeof params.checkPoint_id,
        form_id: typeof params.form_id,
        user_id: typeof userId,
        form_data: typeof formDataJson,
      },
    });

    // Verify asset exists
    const checkPointCheck = await this.db.exec('SELECT id FROM assets WHERE id = ?', [
      params.checkPoint_id,
    ]);
    if (checkPointCheck.length === 0) {
      throw new Error(`Asset not found: ${params.checkPoint_id}`);
    }

    // Verify interaction type exists
    const typeCheck = await this.db.exec('SELECT id FROM interaction_types WHERE id = ?', [
      params.form_id,
    ]);
    if (typeCheck.length === 0) {
      throw new Error(`Interaction type not found: ${params.form_id}`);
    }

    const now = Date.now();
    await this.db.run(
      `INSERT INTO interaction_records (id, asset_id, interaction_type_id, asset_instance_id, data, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        params.checkPoint_id,
        params.form_id,
        params.asset_instance_id || '',
        formDataJson,
        userId,
        now,
        userId,
        now,
      ],
    );

    // Get the created record to return with the actual created_at timestamp
    const records = await this.db.exec('SELECT * FROM interaction_records WHERE id = ?', [id]);

    if (records.length === 0) {
      throw new Error('Failed to retrieve created interaction record');
    }

    const record = records[0];
    return this.mapRowToFormSubmission(record);
  }

  /**
   * Update an existing interaction record
   * Note: In practice, FormSubmission are usually immutable once created,
   * but this is provided for completeness
   */
  async update(id: string, params: UpdateInteractionParams): Promise<void> {
    if (params.form_data !== undefined) {
      // Extract binary data to BLOBs
      const processedFormData = await this.extractBinaryToBLOBs(params.form_data);
      const formDataJson = JSON.stringify(processedFormData);

      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        throw new Error('User must be authenticated to update FormSubmission');
      }

      const now = Date.now();
      await this.db.run(
        'UPDATE interaction_records SET data = ?, updated_by = ?, updated_at = ? WHERE id = ?',
        [formDataJson, userId, now, id],
      );
    }
  }

  /**
   * Delete an interaction record
   */
  async delete(id: string): Promise<void> {
    await this.db.run('DELETE FROM interaction_records WHERE id = ?', [id]);
  }

  /**
   * Map database row to InteractionRecord model
   */
  private async mapRowToFormSubmission(row: any): Promise<FormSubmissionRecord> {
    const formData = this.parseFormDataString(row.data);

    // Resolve BLOB references
    const resolvedFormData = await this.resolveBLOBReferences(formData);

    return {
      id: row.id,
      checkPoint_id: row.asset_id,
      form_id: row.interaction_type_id,
      asset_instance_id: row.asset_instance_id || undefined,
      form_data: resolvedFormData,
      created_at: new Date(row.created_at).toISOString(),
    };
  }

  /**
   * Parse form_data JSON string to object
   */
  private parseFormDataString(formDataString: string): Record<string, any> {
    try {
      return typeof formDataString === 'string' ? JSON.parse(formDataString) : formDataString;
    } catch (e) {
      this.logger.error('Failed to parse form_data:', e);
      return {};
    }
  }

  /**
   * Parse form_data JSON string to object (legacy public method)
   */
  parseFormData(interaction: FormSubmissionRecord): Record<string, any> {
    return interaction.form_data || {};
  }

  /**
   * Extract binary data from form data and store in form_blobs table
   * Returns form data with BLOB references instead of inline data
   */
  private async extractBinaryToBLOBs(formData: Record<string, any>): Promise<Record<string, any>> {
    const result = { ...formData };

    for (const [key, value] of Object.entries(formData)) {
      if (Array.isArray(value)) {
        // Handle photo arrays
        result[key] = await Promise.all(
          value.map((v) => (this.isBinaryDataURL(v) ? this.storeBLOBAndGetReference(v) : v)),
        );
      } else if (this.isBinaryDataURL(value)) {
        result[key] = await this.storeBLOBAndGetReference(value);
      }
    }

    return result;
  }

  /**
   * Check if value is a binary data URL
   */
  private isBinaryDataURL(value: any): boolean {
    if (typeof value !== 'string') return false;

    return (
      value.startsWith('data:image/') ||
      value.startsWith('data:application/pdf') ||
      value.startsWith('data:application/') // Other file types
    );
  }

  /**
   * Compute SHA-256 hash of data URL
   */
  private async computeHash(dataUrl: string): Promise<string> {
    // Extract base64 data (remove "data:image/jpeg;base64," prefix)
    const base64Data = dataUrl.split(',')[1];

    // Decode base64 to binary
    const binaryStr = atob(base64Data);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    // Compute SHA-256 hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }

  /**
   * Extract MIME type from data URL
   */
  private getMimeType(dataUrl: string): string {
    const match = dataUrl.match(/^data:([^;]+)/);
    return match ? match[1] : 'application/octet-stream';
  }

  /**
   * Store BLOB and return reference string.
   *
   * Uses INSERT OR IGNORE to handle the race condition where two concurrent calls
   * with the same hash both pass the "does it exist?" check before either INSERT
   * completes. After the INSERT (or ignore), we always increment ref_count so the
   * count stays accurate regardless of which path was taken.
   */
  private async storeBLOBAndGetReference(dataUrl: string): Promise<string> {
    const hash = await this.computeHash(dataUrl);

    // Extract data upfront (needed for INSERT)
    const base64Data = dataUrl.split(',')[1];
    const mimeType = this.getMimeType(dataUrl);
    const sizeBytes = Math.ceil(base64Data.length * 0.75); // Approximate binary size
    const now = Date.now();

    // INSERT OR IGNORE: silently skips if hash already exists (race condition or
    // legitimate dedup). ref_count starts at 0 so the UPDATE below always brings
    // it to the correct value whether this INSERT ran or was ignored.
    await this.db.run(
      `INSERT OR IGNORE INTO form_blobs (hash, data, mime_type, size_bytes, ref_count, created_at)
       VALUES (?, ?, ?, ?, 0, ?)`,
      [hash, base64Data, mimeType, sizeBytes, now],
    );

    // Always increment ref_count — covers both the new-insert and the reuse paths
    await this.db.run('UPDATE form_blobs SET ref_count = ref_count + 1 WHERE hash = ?', [hash]);

    this.logger.debug('Stored BLOB:', hash.substring(0, 8), mimeType, sizeBytes, 'bytes');
    return `blob:sha256:${hash}`;
  }

  /**
   * Resolve BLOB references in form data back to data URLs
   */
  private async resolveBLOBReferences(formData: Record<string, any>): Promise<Record<string, any>> {
    // Guard against null/undefined formData
    if (!formData || typeof formData !== 'object') {
      return {};
    }

    const result = { ...formData };

    for (const [key, value] of Object.entries(formData)) {
      if (Array.isArray(value)) {
        result[key] = await Promise.all(
          value.map((v) => (this.isBLOBReference(v) ? this.loadBLOB(v) : v)),
        );
      } else if (this.isBLOBReference(value)) {
        result[key] = await this.loadBLOB(value);
      }
    }

    return result;
  }

  /**
   * Check if value is a BLOB reference
   */
  private isBLOBReference(value: any): boolean {
    return typeof value === 'string' && value.startsWith('blob:sha256:');
  }

  /**
   * Load BLOB data by hash reference
   */
  private async loadBLOB(reference: string): Promise<string> {
    // Extract hash from "blob:sha256:abc123..."
    const hash = reference.replace('blob:sha256:', '');

    const results = await this.db.exec('SELECT data, mime_type FROM form_blobs WHERE hash = ?', [
      hash,
    ]);

    if (results.length === 0) {
      this.logger.error('BLOB not found:', hash.substring(0, 8));
      return ''; // Return empty string if BLOB missing
    }

    const { data, mime_type } = results[0];
    return `data:${mime_type};base64,${data}`;
  }
}
